import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, MessagesSquare, SquarePen } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STARTERS = [
  "Aku punya ide bikin konten soal 'morning routine', tapi bingung angle-nya. Bantu tajamin dong.",
  "Kasih 5 ide konten yang lagi relevan buat niche aku minggu ini.",
  "Aku mau bikin series konten. Gimana cara bikin temanya nyambung tapi tetap variatif?",
];

function Bubble({ role, content, streaming }) {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? "bg-sk-brand text-sk-ink font-medium rounded-br-md"
          : "bg-sk-surface border border-sk-line text-sk-cream rounded-bl-md"
      }`}>
        {content}
        {streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-sk-brand animate-pulse rounded-sm" />}
      </div>
    </motion.div>
  );
}

export default function Brainstorming() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [identityId, setIdentityId] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  useEffect(() => {
    api.get("/brainstorm/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/identities").then((r) => {
      setProfiles(r.data);
      if (r.data.length > 0) setIdentityId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    if (inputRef.current) { inputRef.current.style.height = "auto"; }
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);

    try {
      const res = await fetch(`${API}/brainstorm/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, identity_id: identityId || null }),
      });
      if (!res.ok || !res.body) throw new Error("no stream");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let errored = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try { data = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (data.delta) {
            acc += data.delta;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: acc, streaming: true };
              return copy;
            });
          } else if (data.error) {
            errored = true;
            toast.error(data.error);
          }
        }
      }

      setMessages((m) => {
        const copy = [...m];
        if (errored && !acc) {
          copy.pop(); // remove empty assistant bubble
        } else {
          copy[copy.length - 1] = { role: "assistant", content: acc };
        }
        return copy;
      });
    } catch (e) {
      toast.error("Yah, ada gangguan pas nyambung ke AI. Coba lagi ya!");
      setMessages((m) => m.filter((x) => !(x.role === "assistant" && x.streaming)));
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    await api.delete("/brainstorm/messages");
    setMessages([]);
    toast.success("Riwayat brainstorming dibersihkan");
  };

  const activeProfile = profiles.find((p) => p.id === identityId);
  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100dvh-7.5rem)] md:h-[calc(100vh-6rem)]">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Partner Kreatif</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Brainstorming</h1>
          <p className="text-sk-muted mt-2 max-w-xl leading-relaxed text-sm sm:text-base">Lempar premis kasar atau ide setengah jadi — AI bantu asah jadi ide konten yang lebih tajam & spesifik sesuai brand kamu.</p>
        </div>
        <div className="flex items-center gap-2">
          {profiles.length > 0 && (
            <Select value={identityId} onValueChange={setIdentityId}>
              <SelectTrigger className="w-[170px] bg-sk-surface border-sk-line rounded-xl text-sk-cream" data-testid="brainstorm-dna-select">
                <SelectValue placeholder="Pilih DNA brand" />
              </SelectTrigger>
              <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
                {profiles.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`brainstorm-dna-opt-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {!isEmpty && (
            <button onClick={clearChat} data-testid="clear-chat-btn" className="flex items-center gap-1.5 rounded-xl border border-sk-line bg-sk-surface px-3 py-2 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
              <Trash2 className="h-4 w-4" /> Bersihkan
            </button>
          )}
        </div>
      </div>

      {profiles.length === 0 && (
        <div className="mb-3 rounded-xl border border-sk-line bg-sk-surface p-3 text-xs text-sk-muted leading-relaxed" data-testid="brainstorm-no-dna">
          Belum ada DNA brand. Buat dulu di menu <span className="text-sk-brand font-semibold">Identitas Kreator</span> biar saran AI nyambung sama gaya kamu.
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 min-h-[300px] overflow-y-auto rounded-2xl border border-sk-line bg-sk-bg p-4 sm:p-6 space-y-4">
        {isEmpty ? (
          <div className="flex flex-col items-center text-center px-1 py-8 sm:py-12">
            <div className="h-14 w-14 rounded-2xl bg-sk-brand/12 flex items-center justify-center mb-4">
              <MessagesSquare className="h-7 w-7 text-sk-brand" />
            </div>
            <h3 className="font-bold text-lg text-sk-cream">Mulai brainstorming</h3>
            <p className="text-sk-muted text-sm mt-2 max-w-sm leading-relaxed">Ceritain ide kamu, sekasar apapun. Atau coba salah satu pemantik di bawah ini.</p>
            <div className="mt-6 grid gap-3 w-full max-w-lg">
              {STARTERS.map((s, i) => (
                <button key={i} data-testid={`starter-${i}`} onClick={() => send(s)} className="text-left rounded-xl border border-sk-line bg-sk-surface p-4 text-sm text-sk-cream hover:border-sk-brand hover:-translate-y-0.5 transition-transform duration-200">
                  <Sparkles className="h-4 w-4 text-sk-brand inline mr-2" />{s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} streaming={m.streaming} />)
        )}
      </div>

      {/* Composer — floating rounded pill */}
      <div className="mt-4">
        <div className="flex items-end gap-2 rounded-[26px] bg-sk-surface border border-sk-line p-2 shadow-lg shadow-black/5 focus-within:ring-2 focus-within:ring-sk-brand focus-within:border-transparent transition-shadow">
          <button
            onClick={() => { if (!isEmpty) clearChat(); }}
            data-testid="new-chat-btn"
            title="Obrolan baru"
            aria-label="Obrolan baru"
            className="h-11 w-11 shrink-0 rounded-full border border-sk-line text-sk-muted flex items-center justify-center hover:text-sk-brand hover:border-sk-brand transition-colors"
          >
            <SquarePen className="h-5 w-5" />
          </button>
          <textarea
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Tulis ide kamu di sini…"
            className="flex-1 resize-none bg-transparent border-0 px-1 py-2.5 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-0 max-h-40 overflow-y-auto leading-relaxed"
          />
          <button
            onClick={() => send()}
            disabled={sending || !input.trim()}
            data-testid="send-chat-btn"
            className="h-11 w-11 shrink-0 rounded-full bg-sk-brand text-sk-ink flex items-center justify-center hover:bg-sk-brandDark transition-colors disabled:opacity-50"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
      {activeProfile && <p className="text-xs text-sk-muted mt-2 px-1">Saran disesuaikan dengan <span className="text-sk-brand font-semibold">{activeProfile.name}</span>.</p>}
    </div>
  );
}
